import { IS_FR, tr } from '@/lib/i18n';
import { useNavigate, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { useHaptic } from '@/hooks/useHaptic';
import { useCoachUnread } from '@/hooks/useCoachUnread';
import { useMemo } from 'react';
import {
  Home,
  BookOpen,
  MessageCircle,
  Library as LibraryIcon,
  User,
  Sparkles,
} from 'lucide-react';

interface NavItem {
  icon: typeof Home;
  label: string;
  path: string;
}

const NAV_ITEMS: NavItem[] = IS_FR ? [
  { icon: Home, label: tr('Home'), path: '/' },
  { icon: BookOpen, label: tr('The 3 Magic Words'), path: '/magic-words' },
  { icon: MessageCircle, label: tr('Sarah'), path: '/expert-chat' },
  { icon: User, label: tr('Profile'), path: '/profile' },
] : [
  {
    icon: Home,
    label: tr("Home"),
    path: '/',
  },
  {
    icon: BookOpen,
    // 22/09: o mesmo nome da barra inferior. "Magic Words" é o card da home.
    label: tr("Phrases"),
    path: '/scripts',
  },
  {
    icon: MessageCircle,
    label: tr("Sarah"),
    path: '/expert-chat',
  },
  {
    // 24/09: a aba é só de livros agora (as vídeo-aulas saíram). O endereço
    // continua /learn para não quebrar link salvo; o nome e o ícone dizem o que tem.
    icon: LibraryIcon,
    label: tr("Books"),
    path: '/learn',
  },
  {
    icon: User,
    label: tr("Profile"),
    path: '/profile',
  },
];

// 22/09/2026: mesmos estados da barra inferior (ver BottomNavExpandable):
// aba atual em cápsula no acento, as outras neutras; sem degradê por item,
// brilho, faísca, ciano permanente da Sarah ou hover laranja.
function SidebarNavItem({ item, isActive, badge }: {
  item: NavItem;
  isActive: boolean;
  badge?: number;
}) {
  const navigate = useNavigate();
  const { triggerHaptic } = useHaptic();
  const Icon = item.icon;

  return (
    <button
      type="button"
      onClick={() => {
        triggerHaptic('light');
        navigate(item.path);
      }}
      className={cn(
        "relative flex items-center gap-3 w-full min-h-12 px-4 py-3 rounded-xl transition-colors duration-200",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
        isActive ? "bg-accent/10" : "hover:bg-muted"
      )}
      aria-label={item.label}
      aria-current={isActive ? 'page' : undefined}
    >
      <span className="relative">
        <Icon className={cn("w-5 h-5", isActive ? "text-accent" : "text-muted-foreground")} strokeWidth={isActive ? 2.6 : 2}
          fill={isActive ? "currentColor" : "none"}
          fillOpacity={isActive ? 0.2 : 0} />
        {(badge ?? 0) > 0 && !isActive && (
          <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-background" />
        )}
      </span>
      <span className={cn("font-semibold text-[15px]", isActive ? "text-accent" : "text-muted-foreground")}>
        {item.label}
      </span>
    </button>
  );
}

export function SidebarNav() {
  const location = useLocation();
  const coachUnread = useCoachUnread();

  const activeIndex = useMemo(() => {
    if (location.pathname === '/') return 0;
    const idx = NAV_ITEMS.findIndex(item =>
      item.path !== '/' && location.pathname.startsWith(item.path)
    );
    return idx >= 0 ? idx : null;
  }, [location.pathname]);

  return (
    <aside
      aria-label={tr("Main navigation")}
      className="hidden md:flex fixed left-0 top-0 bottom-0 z-40 w-60 lg:w-64 flex-col"
    >
      {/* Background with blur */}
      <div className="absolute inset-0 bg-card/95 backdrop-blur-2xl border-r border-border/50" />

      {/* Content */}
      <div className="relative z-10 flex flex-col h-full">
        {/* Logo/Brand section */}
        <div className="px-5 py-6 border-b border-border/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-accent/10 flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-accent" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-foreground">{tr("Obedience Code")}</h1>
              <p className="text-[15px] text-muted-foreground">{tr("Parenting Made Easy")}</p>
            </div>
          </div>
        </div>

        {/* Navigation Items */}
        <nav className="flex-1 px-3 py-4 space-y-1.5 overflow-y-auto">
          {NAV_ITEMS.map((item, index) => (
            <SidebarNavItem
              key={item.path}
              item={item}
              isActive={activeIndex === index}
              badge={item.path === '/expert-chat' ? coachUnread : undefined}
            />
          ))}
        </nav>

        {/* Footer section */}
        <div className="px-5 py-4 border-t border-border/50">
          <div className="text-[15px] text-muted-foreground text-center">{tr("© 2026 Obedience Code")}</div>
        </div>
      </div>
    </aside>
  );
}
